import Phaser from 'phaser';

/**
 * Lightweight Phaser-native text input — a drop-in replacement for the HTML `<input>`
 * overlays we used to pin over the canvas. Avoids DOM/canvas sync bugs (position drift on
 * resize, stale elements after scene transitions, focus leaks) by living entirely inside
 * the Phaser scene graph. Handles: click-to-focus, keyboard typing, Backspace, blinking
 * cursor, blur on Esc or click-outside, and a placeholder.
 *
 * Not a general-purpose text editor — no selection, no copy/paste, no multi-line — this
 * is deliberately minimal so it stays predictable for inventory-style search fields.
 */
export interface PhaserTextInputOpts {
  x: number;
  y: number;
  width: number;
  height: number;
  placeholder?: string;
  initialValue?: string;
  fontSize?: string;
  maxLength?: number;
  depth?: number;
  /** Invoked on every change (typing + backspace). */
  onChange?: (value: string) => void;
}

export class PhaserTextInput {
  private scene: Phaser.Scene;
  private bg: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private text: Phaser.GameObjects.Text;
  private placeholder: Phaser.GameObjects.Text;
  private cursor: Phaser.GameObjects.Rectangle;
  private cursorTimer?: Phaser.Time.TimerEvent;
  private value: string;
  private opts: PhaserTextInputOpts;
  private focused = false;
  private keyHandler?: (ev: KeyboardEvent) => void;
  private outsideClickHandler?: (p: Phaser.Input.Pointer) => void;

  constructor(scene: Phaser.Scene, opts: PhaserTextInputOpts) {
    this.scene = scene;
    this.opts = opts;
    this.value = opts.initialValue ?? '';
    const depth = opts.depth ?? 40;
    const { x, y, width, height } = opts;

    this.bg = scene.add.rectangle(x, y, width, height, 0x0d0a08, 1)
      .setOrigin(0, 0).setDepth(depth);
    this.border = scene.add.rectangle(x, y, width, height)
      .setOrigin(0, 0).setStrokeStyle(1, 0x6a3a00, 0.95).setDepth(depth + 1);

    const fontSize = opts.fontSize ?? '10px';
    const textY = y + Math.round(height / 2);
    this.text = scene.add.text(x + 8, textY, this.value, {
      fontSize, fontFamily: 'Palatino Linotype, Palatino, Georgia, serif', fontStyle: 'italic',
      color: '#d8a03a',
    }).setOrigin(0, 0.5).setDepth(depth + 2).setResolution(2);
    this.placeholder = scene.add.text(x + 8, textY, opts.placeholder ?? '', {
      fontSize, fontFamily: 'Palatino Linotype, Palatino, Georgia, serif', fontStyle: 'italic',
      color: '#5a3600',
    }).setOrigin(0, 0.5).setDepth(depth + 2).setResolution(2);

    this.cursor = scene.add.rectangle(x + 8 + this.text.width, textY, 1, Math.round(height * 0.6), 0xd8a03a, 1)
      .setOrigin(0, 0.5).setDepth(depth + 2).setVisible(false);

    this.bg.setInteractive({ useHandCursor: true });
    this.bg.on('pointerdown', () => this.focus());

    this.updateVisuals();

    scene.events.once('shutdown', () => this.destroy());
    scene.events.once('destroy',  () => this.destroy());
  }

  focus(): void {
    if (this.focused) return;
    this.focused = true;
    this.border.setStrokeStyle(1, 0xc8973a, 1);
    // Keep Phaser's own keyboard listeners from swallowing characters while the input is hot.
    this.scene.input.keyboard?.disableGlobalCapture();
    // Dedicated raw DOM listener — we pull `ev.key` directly instead of mapping Phaser key codes,
    // which correctly handles AZERTY/QWERTY/symbols without maintaining a translation table.
    this.keyHandler = (ev: KeyboardEvent): void => {
      if (!this.focused) return;
      if (ev.key === 'Escape') { this.blur(); return; }
      if (ev.key === 'Backspace') {
        if (this.value.length > 0) {
          this.value = this.value.slice(0, -1);
          this.emitChange();
          this.updateVisuals();
        }
        ev.preventDefault();
        return;
      }
      if (ev.key.length === 1) {
        const max = this.opts.maxLength ?? 64;
        if (this.value.length < max) {
          this.value += ev.key;
          this.emitChange();
          this.updateVisuals();
        }
        ev.preventDefault();
      }
    };
    window.addEventListener('keydown', this.keyHandler, true);

    // Blur when the player clicks anywhere outside the input's rectangle.
    this.outsideClickHandler = (p: Phaser.Input.Pointer): void => {
      const { x, y } = this.bg;
      const w = this.bg.width, h = this.bg.height;
      if (p.x < x || p.x > x + w || p.y < y || p.y > y + h) this.blur();
    };
    this.scene.input.on('pointerdown', this.outsideClickHandler);

    this.startCursorBlink();
  }

  blur(): void {
    if (!this.focused) return;
    this.focused = false;
    this.border.setStrokeStyle(1, 0x6a3a00, 0.95);
    this.scene.input.keyboard?.enableGlobalCapture();
    if (this.keyHandler) {
      window.removeEventListener('keydown', this.keyHandler, true);
      this.keyHandler = undefined;
    }
    if (this.outsideClickHandler) {
      this.scene.input.off('pointerdown', this.outsideClickHandler);
      this.outsideClickHandler = undefined;
    }
    this.stopCursorBlink();
    this.cursor.setVisible(false);
  }

  getValue(): string { return this.value; }

  setValue(v: string): void {
    this.value = v;
    this.updateVisuals();
  }

  setVisible(v: boolean): void {
    this.bg.setVisible(v);
    this.border.setVisible(v);
    this.text.setVisible(v);
    this.placeholder.setVisible(v && this.value.length === 0);
    if (!v) this.blur();
  }

  destroy(): void {
    this.blur();
    this.bg.destroy();
    this.border.destroy();
    this.text.destroy();
    this.placeholder.destroy();
    this.cursor.destroy();
  }

  private emitChange(): void {
    this.opts.onChange?.(this.value);
  }

  private updateVisuals(): void {
    this.text.setText(this.value);
    this.placeholder.setVisible(this.value.length === 0);
    // Park the cursor right after the last character.
    this.cursor.setX(this.text.x + this.text.width);
  }

  private startCursorBlink(): void {
    this.stopCursorBlink();
    this.cursor.setVisible(true);
    this.cursorTimer = this.scene.time.addEvent({
      delay: 500, loop: true, callback: () => this.cursor.setVisible(!this.cursor.visible),
    });
  }

  private stopCursorBlink(): void {
    this.cursorTimer?.remove(false);
    this.cursorTimer = undefined;
  }
}
